import { z } from 'zod';
import { type AgentResponseTone } from 'store/ai-chatbot';
import {
  type ClientFeature,
  type DepositManagementMethod,
} from 'store/admin-clients';
import { type AuthProvider } from 'store/auth';
import { type DiscountPackage } from 'store/discounts';
import { type UploadedFile } from 'store/files';
import { Translations } from 'types/Common';
import { ContentLanguage, Currency } from 'types/general';
import {
  companiesFiltersSchema,
  parentCompaniesFiltersSchema,
} from './companies.schemas';

export const companyStatuses = ['active', 'inactive'] as const;
export type CompanyStatus = (typeof companyStatuses)[number];

export const sourcesOfRecognitionPoints = ['melp', 'externalProvider'] as const;
export type SourceOfRecognitionPoints =
  (typeof sourcesOfRecognitionPoints)[number];

export const claimBenefitCombinations = [
  'any',
  'sameCategory',
  'noCombining',
] as const;
export type ClaimBenefitCombination = (typeof claimBenefitCombinations)[number];

export interface BaseCompany {
  id: string;
  name: string;
  code: string;
  status: CompanyStatus;
}

export interface ListCompany extends BaseCompany {
  employeeCount: number;
  departmentCount: number;
}

export interface CompanyManager {
  id: string;
  name: string;
}

export interface Company extends BaseCompany {
  country: string;
  currency: Currency;
  logoId: string;
  additionalInfo: string;
  manager: CompanyManager | null;
  billingInfo: string;
  supportedLanguages: Uppercase<ContentLanguage>[];
  creationDate: string;
  activationDate: string | null;
  deactivationDate: string | null;
  departments: Array<{ id: string; name: string; hidden: boolean }>;
  branding: {
    colors: string[];
  };
  settings: {
    sourceOfRecognitionPoints: SourceOfRecognitionPoints;
    canGiveRecognition: boolean;
    hidePointBalanceMobile: boolean;
    accountantEmails: string[];
    accountantLanguage: Uppercase<ContentLanguage>;
  };
}

export interface CompanyContact {
  id: string;
  phone: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
}

export interface BaseParentCompany {
  id: string;
  country: string;
  internalComment: string;
  name: string;
  status: CompanyStatus;
  activeEmployees: number;
  activeMobileUsers: number;
  inactiveEmployees: number;
}

export interface ParentCompany extends BaseParentCompany {
  code: string;
  defaultCurrency: Currency;
  creationDate: string;
  deactivationDate: string | null;
  supportedLanguages: Uppercase<ContentLanguage>[];
  features: ClientFeature[];
  companies: Company[];
  logo: UploadedFile | null;
  branding: {
    colors: string[];
  };
  billingInfo: string;
  settings: {
    defaultLanguage: Uppercase<ContentLanguage>;
    isPersonalCodeRequiredForLogin: boolean;
    isEmailVisibleInternally: boolean;
    allowChangeProfilePicture: boolean;
    discountPlan: DiscountPackage;
    depositManagementMethod: DepositManagementMethod;
    useExternalId: boolean;
    accountantEmails: string[];
    accountantLanguage: Uppercase<ContentLanguage>;
    merchandiseContactPersonName: string;
    merchandiseContactPersonPhone: string;
    merchandiseContactPersonEmail: string;
    companyExpenseEnabled: boolean;
    companyExpenseEmail: string;
    expenseCompensationAskIban: boolean;
    authProvider: AuthProvider;
    authProviderName: string;
    sendEmployeeWelcomeMessage: boolean;
    conversationalAgentId: string;
    ragId: string;
    ragDescription: string;
    conversationalAgentTranslations: Translations<{
      name: string;
      greeting: string;
    }>;
    agentResponseTone: AgentResponseTone;
    agentShowCitations: boolean;
    expenseCompensationBenefitCombination: ClaimBenefitCombination;
    ipWhitelistEnabled: boolean;
    ipWhitelist: string[];
    sessionTimeoutEnabled: boolean;
    sessionTimeoutInMinutes: number;
  };
}

export type ParentCompanyWithStats = Pick<
  ParentCompany,
  'id' | 'name' | 'country' | 'internalComment' | 'status'
> & {
  activeEmployees: number;
  activeMobileUsers: number;
  inactiveEmployees: number;
};

export type CompaniesFilters = z.infer<typeof companiesFiltersSchema>;

export type ParentCompaniesFilters = z.infer<
  typeof parentCompaniesFiltersSchema
>;
